import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import MediaField from '@/components/organisms/Dialogs/MediaField/MediaField';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDialogAlert from '@/hooks/useDialogAlert';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import { ACCEPTED_FILE_FORMATS } from '@/redux/dialogs/helper';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { ACCEPTED_IMAGE_FORMATS, ACCEPTED_VIDEO_FORMATS } from '@/util/constants';
import { getFormErrorsKey } from '@/util/util';
import { fileActionSchemaNew } from '@/util/validation/schemas/action-schemas';

import ToolkitWrapper from '../ToolkitWrapper';

const ACCEPTED_FORMATS = {
  file: ACCEPTED_FILE_FORMATS,
  image: ACCEPTED_IMAGE_FORMATS,
  video: ACCEPTED_VIDEO_FORMATS,
};

export type MediaForm = { url: string; name: string };

// Toolkit form for image, video and file action
const ToolkitActionMedia = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { type, actionId, url, name } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<
      Action,
      { type: 'file' | 'image' | 'video' }
    >;
    return {
      type: selectedAction.type,
      actionId: selectedAction.action_id,
      url: selectedAction.url,
      name: selectedAction.name,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const form = useForm<MediaForm>({
    mode: 'onChange',
    defaultValues: { url, name },
    resolver: yupResolver(fileActionSchemaNew) as Resolver<MediaForm>,
  });
  const {
    control,
    getValues,
    setValue,
    trigger,
    setFocus,
    formState: { errors, isDirty },
  } = form;
  const urlValue = useWatch({ control, name: 'url' });

  useFocusOnInput('name', errors, setFocus);

  const handleChange = useCallback(() => {
    const values = getValues();

    dispatch(updateAction({ actionId, action: { ...values } }));
  }, [getValues, dispatch, actionId]);

  const onFileRemoved = useCallback(() => {
    setValue('url', '', { shouldDirty: true });
    dispatch(updateAction({ actionId, action: { url: '' } }));
  }, [setValue, dispatch, actionId]);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t(`actions.types.${type}`),
    type,
  });

  useEffect(() => {
    trigger();
  }, [trigger, url]);

  // Only surface errors after the user interacts with the form
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const fields = ['url', 'name'] as const;

    for (const field of fields) {
      setFieldError(field, errors[field]?.message);
    }
  }, [getFormErrorsKey(errors), isDirty, setFieldError]);

  return (
    <ToolkitWrapper type={type}>
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <MediaField
            error={!!errors.url}
            errorMessage={errors.url?.message}
            onChange={handleChange}
            setValue={setValue}
            name="url"
            urlValue={urlValue}
            onFileRemoved={onFileRemoved}
            accept={ACCEPTED_FORMATS[type]}
          />

          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('common.name')}
                  <InfoTooltip content={t(`dialog.${type}.name_tooltip`)} side="right">
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      handleChange();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionMedia;
